import {Component, inject, OnInit, signal} from '@angular/core';
import {FormsModule} from '@angular/forms';
import {NgbPagination} from '@ng-bootstrap/ng-bootstrap';
import {SignLogService} from '../sign-log.service';
import {parseSignLogReference} from '../sign-log-reference';
import {SignatureLogPageData} from '../../../../../generated-model/models';
import {PageResponse} from '../../../../shared/commons/page-response';
import {ThaiDateTimePipe} from '../../../../shared/commons/thai-datetime.pipe';
import {PageTitleComponent} from '../../../../shared/commons/page-title/page-title.component';
import {PagesizeSelectComponent} from '../../../../shared/components/pagesize-select/pagesize-select.component';

@Component({
  selector: 'app-sign-log-list',
  standalone: true,
  imports: [FormsModule, NgbPagination, ThaiDateTimePipe, PageTitleComponent, PagesizeSelectComponent],
  templateUrl: './sign-log-list.component.html',
  styleUrl: './sign-log-list.component.scss'
})
export class SignLogListComponent implements OnInit {
  private readonly service = inject(SignLogService);

  protected term = signal<string>('');
  protected action = signal<string>('');
  protected status = signal<string>('');
  /** จำนวนวันที่เก็บ Log ไว้ มาจาก app.signature-log.retention-days ของฝั่งเซิร์ฟเวอร์ */
  protected retentionDays = signal<number>(150);
  protected pageData = signal<PageResponse<SignatureLogPageData>>({
    page: {size: 20, number: 0}
  } as PageResponse<SignatureLogPageData>);

  readonly ACTION_LABELS: Record<string, string> = {
    getURI:   'เรียกโปรแกรมลงลายมือชื่อ',
    getData:  'ดึงข้อมูลใบคำขอมา Sign',
    postData: 'ลงลายมือชื่ออิเล็กทรอนิกส์',
  };

  ngOnInit(): void {
    this.service.settings().subscribe({
      next: (res) => this.retentionDays.set(res.retentionDays),
      error: (err) => console.error(err)
    });
    this.search();
  }

  search(page: number = 1): void {
    const params: Record<string, string | number> = {
      page: page - 1,
      size: this.pageData().page.size,
      sort: 'dateTime,desc'
    };
    if (this.term().trim()) {
      params['term'] = this.term().trim();
    }
    if (this.action()) {
      params['action'] = this.action();
    }
    if (this.status()) {
      params['status'] = this.status();
    }
    this.service.search(params).subscribe({
      next: (res) => this.pageData.set(res),
      error: (err) => console.error(err)
    });
  }

  reset(): void {
    this.term.set('');
    this.action.set('');
    this.status.set('');
    this.search();
  }

  onPageSizeChange(size: number): void {
    this.pageData.update(data => ({...data, page: {...data.page, size}}) as PageResponse<SignatureLogPageData>);
    this.search();
  }

  parseReference(ref: string | undefined): [string, string][] {
    return parseSignLogReference(ref);
  }

  /**
   * dateTime มาจาก backend เป็น ISO-8601 instant (ลงท้ายด้วย Z)
   * ThaiDateTimePipe แยกสตริงเองด้วย split(/[^0-9]/) ซึ่งอ่านรูปแบบนี้ผิด
   * (ได้เวลา UTC มาแสดงเป็นเวลาไทย หรือกลายเป็น 00:00 เมื่อมีเศษวินาที)
   * จึงต้องแปลงเป็น Date ก่อน เพื่อให้ pipe ใช้สาขาที่แปลง timezone ถูกต้อง
   */
  toDate(value: Date | string | null | undefined): Date | null {
    if (!value) {
      return null;
    }
    const date = value instanceof Date ? value : new Date(value);
    return Number.isNaN(date.getTime()) ? null : date;
  }

  statusBadgeClass(status: string | undefined): string {
    const normalized = (status ?? '').toLowerCase();
    if (normalized === 'success') {
      return 'success';
    }
    if (normalized === 'error') {
      return 'danger';
    }
    return 'secondary';
  }
}
